import { ReactElement, useCallback, useEffect, useState, useRef } from "react";
import { api, ApiStateSetter } from "../../hooks/use-api-state";
import styles from "./ContentLessonSelector.module.css";
import { IChapter, IContentData, CODEREVIEW, PRESENTATION, IModule, SlideVisitedStatisticsDict, VisitedSlides, ASSESSMENT } from "../../messages/IGetUserContentResponse";
import { IAssessmentObject, IAssessmentResponse } from "../../messages/IAssessments";
import { useSearchParams } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { LessonKey } from "./Content";
import { useNavigate } from "react-router-dom";
import { IUserInfo } from "../../messages/IAppInfo";
import { hasPermission } from "../common/ProtectedRoute";
//import { on } from "events";

type ChaptersShown = { [chapterIndex: string]: boolean };

export default function ContentItemSelector(props: {
  selectedLesson: LessonKey | null,
  courseData: IContentData,
  visitedSlides: VisitedSlides,
  onSlideVisited: (chapterId:string, moduleId:string, slideId:string, visited:boolean) => void, // Used to "un-visit" a slide (module)
  codeReviewOnly: boolean,
  // Assessment related
  setGetAssessmentApiState: ApiStateSetter,
  busy: boolean,
  setMessage: React.Dispatch<React.SetStateAction<string | null>>,
  setAssessment: React.Dispatch<React.SetStateAction<IAssessmentResponse | null>>
}): ReactElement {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const contentStructure = props.courseData.content_structure;
  const prevSelectedChapter = useRef<IChapter | null>(null);

  const [structureId, setStructureId] = useState<string>(contentStructure.id);
  const [chaptersShown, setChaptersShown] = useState<ChaptersShown>({});
  const [selectedChapter, setSelectedChapter] = useState<IChapter|null>(null);
  const [areLessonsCollapsed, setAreLessonsCollapsed] = useState<boolean>(false);

  const [assessment, setAssessment] = useState<IAssessmentResponse | null>(null);

  function checkIfModuleIsFinishedByTheUser(contentKey: string, moduleKey: string) 
  {
    let ret = false;
    if (!!props.visitedSlides[contentKey]) {
      const visitedModuleStatistics = props.visitedSlides[contentKey][moduleKey] as SlideVisitedStatisticsDict;
      if (!!visitedModuleStatistics) {
        const visitedEnd = visitedModuleStatistics["_END"];
        if (typeof visitedEnd === 'number') {
          ret = visitedEnd > 0;
        } else if (typeof visitedEnd === 'object') {
          ret = (visitedEnd.visited ?? 0) > 0;
        }
      }
    }
    return ret;
  };


  function hasUnfinishedModules(chapterIndex: string) {
    const chapter = getChapter(chapterIndex);
    if (chapter && chapter.modules) {
      for (const moduleIndex of Object.keys(chapter.modules)) {
        const cohortCode = contentStructure.content_cohort_code ?? "";
        const contentId = contentStructure.id ?? "";
        const contentKey = `${cohortCode}/${contentId}`;
        const module = chapter.modules[moduleIndex];
        const moduleKey = `${chapter.id}/${module.id}`;
        const done = checkIfModuleIsFinishedByTheUser(contentKey, moduleKey);
        if (!done) {
          return true;
        }
      }
    }
    return false;
  }


  const initializeChaptersShown = useCallback((shown: boolean|null) => {
    let chaptersShown: ChaptersShown = {};
    if (!!contentStructure && !!contentStructure.chapters) {
      for (const chapterIndex of Object.keys(contentStructure.chapters)) {
        chaptersShown[chapterIndex] = (shown ?? hasUnfinishedModules(chapterIndex)) || (props.codeReviewOnly);
      }
    }
    setChaptersShown(chaptersShown);
  }, [props.codeReviewOnly]);


  function toggleAll() {
    if (areLessonsCollapsed) {
      setAreLessonsCollapsed(false);
      initializeChaptersShown(true);
    } else {
      setAreLessonsCollapsed(true);
      initializeChaptersShown(false);
    }
  }


  function toggleChapterShown(chapterIndex: string) {
    setChaptersShown((prev: ChaptersShown) => {
      let newState = { ...prev };

      let newShown = !prev[chapterIndex];
      newState[chapterIndex] = newShown;

      if (newShown) {
        setAreLessonsCollapsed(false);
      } else {
        let allHidden = true;
        for (const chapterIndex of Object.keys(newState)) {
          if (newState[chapterIndex]) {
            allHidden = false;
            break;
          }
        }
        if (allHidden) {
          setAreLessonsCollapsed(true);
        }
      }
      return newState;
    });
  }


  function selectAssessment(_assessmentObject: IAssessmentObject | null) {
    if (_assessmentObject?.submitted)
      props.setMessage("This assessment has already been submitted.");
    else if (!_assessmentObject?.enabled && _assessmentObject?.disable_text)
      props.setMessage(_assessmentObject?.disable_text);
    else
      props.setMessage(null);

    if (!_assessmentObject?.enabled && !_assessmentObject?.submitted) {
      setAssessment(null);
    }
    else {
      // Submitted or already started => show assessment
      if (_assessmentObject?.submitted || _assessmentObject?.active) {
        showAssessment(_assessmentObject, _assessmentObject?.can_start);
      }
    }
  }


  function chapterClicked(chapterIndex: string, chapter: IChapter) {
    setSelectedChapter(chapter);

    const type = chapter.type ?? PRESENTATION;
    if (type.startsWith(PRESENTATION)) {
      toggleChapterShown(chapterIndex);
    } else if (type.startsWith(ASSESSMENT)) {
        if (chapter.assessment) {
          let assessmentObject = contentStructure.assessments?.[chapter.assessment] || null;
          if (assessmentObject) {
            if (assessmentObject.submitted || assessmentObject.active) {
              // If already submitted or active, just select/show the assessment (if it is not the active one)
              if (searchParams.get("chapter-id") !== chapter.id) {
                selectAssessment(assessmentObject);
              }
            }
            if (assessmentObject.enabled) {
              if (assessmentObject.can_start && !assessmentObject.active) {
                props.setMessage("Press 'Start' to start this assessment.");
              }
            }
            else {
                props.setMessage(assessmentObject.disable_text ?? "This assessment is not enabled.");
            }
          }
        }
    }
  }


  // If a module is clicked, we also select the chapter
  function moduleClicked(chapterIndex: string, chapter: IChapter, moduleIndex: string, module: IModule) {
    setSelectedChapter(chapter);
  }


  function getTogglerIconClass(shown: boolean) {
    if (shown) {
      return "fa-chevron-up";
    } else {
      return "fa-chevron-down";
    }
  }

  function isChapterShown(chapterIndex: string) {
    return !!chaptersShown[chapterIndex];
  }

  function getChapter(chapterIndex: string) {
    if (contentStructure.chapters) {
      return contentStructure.chapters[chapterIndex];
    }
    return null;
  }

  function getModule(chapterIndex: string, moduleIndex: string) {
    const chapter = getChapter(chapterIndex);
    if (chapter) {
      return chapter.modules[moduleIndex];
    }
    return null;
  }

  function unvisitModule(chapterIndex: string, moduleIndex: string) {
    //const cohortCode = contentStructure.content_cohort_code ?? "";
    //const contentId = contentStructure.id ?? "";
    //const contentKey = `${cohortCode}/${contentId}`;
    const chapter = getChapter(chapterIndex);
    const module = getModule(chapterIndex, moduleIndex);
    if (chapter && module) {
      /*
      const moduleId = module.id ?? "";
      const moduleKey = `${chapter.id}/${moduleId}`;
      if (checkIfModuleIsFinishedByTheUser(contentKey, moduleKey)) {
        props.onSlideVisited("_END", false);
      }
      else {
        // Module is not finished, doubleclick has no effect
      }
      */
      props.onSlideVisited(chapter.id, module.id, "_END", false); // Callback function to un-visit a slide (module)
    }
  }

  function getChapterLiClassName(chapterIndex: string, chapter: IChapter) {
    const chapterType = chapter.type ?? PRESENTATION;
    let active = chapterType.startsWith(ASSESSMENT) && props.selectedLesson?.chapterKey === chapterIndex;
    return `${styles.name} ${!!chapter.highlight ? styles.highlight : ""} ${active ? styles.active : ""}`;
  }

  function getLessonLiClassName(chapterIndex: string, chapter: IChapter, moduleIndex: string, module: IModule) {
    let active = props.selectedLesson?.chapterKey === chapterIndex && props.selectedLesson?.moduleKey === moduleIndex;

    const cohortCode = contentStructure.content_cohort_code ?? "";
    const contentId = contentStructure.id ?? "";
    const contentKey = `${cohortCode}/${contentId}`;
    const moduleKey = `${chapter?.id}/${module?.id}`;
    const done = checkIfModuleIsFinishedByTheUser(contentKey, moduleKey);
    const disabled = module.disabled ?? false;
    let className = `${disabled ? styles.disable : ""} ${done ? styles.done : ""} ${active ? styles.active : ""} ${!!module.highlight ? styles.highlight : ""} `;
    return className;
  }

  function getChapterClassName(chapterIndex: string) {
    const shownClass = isChapterShown(chapterIndex) && !areLessonsCollapsed ? styles.shown : "";

    let highlightClass = "";
    if (!!contentStructure.chapters) {
      let chapter = contentStructure.chapters[chapterIndex];
      if (!!chapter && !!chapter.highlight) {
        highlightClass = styles.highlight;
      }
    }

    const className = `${styles.course} ${shownClass} ${highlightClass}`;
    return className;
  }

  function getLessonLink(chapterId: string, moduleId: string|null = null) {
    let sp = new URLSearchParams(searchParams);
    sp.set("chapter-id", chapterId);
    if (moduleId)
      sp.set("lesson-id", moduleId);
    else
      sp.delete("lesson-id");
    return `?${sp.toString()}`;
  }


  useEffect(() => {
    // Leaving an assessment
    if (!!prevSelectedChapter?.current?.assessment) {
      const assessmentObject = contentStructure.assessments?.[prevSelectedChapter.current.assessment] || null;
      if (assessmentObject)
        assessmentObject.shown = false;
    }
    prevSelectedChapter.current = selectedChapter;
  }, [selectedChapter]);


  useEffect(() => {
    setChaptersShown((prev: ChaptersShown) => {
      if (props.selectedLesson === null) {
        return prev;
      }

      let newState = { ...prev };
      newState[props.selectedLesson.chapterKey] = true;

      setAreLessonsCollapsed(false);

      return newState;
    });
  }, [props.selectedLesson]);

  useEffect(() => {
    if (structureId !== contentStructure.id) {
      setStructureId(contentStructure.id);
    }
    initializeChaptersShown(props.codeReviewOnly ? true : null);
  }, [contentStructure.id, props.codeReviewOnly, structureId, setStructureId]);

  function hasCodeReview(chapter: IChapter): boolean {
    if (chapter?.modules)
      return Object.values(chapter?.modules).some(module => module.type?.startsWith(CODEREVIEW));
    else
      return false;
  }


  useEffect(() => {
    props.setAssessment(assessment);
  }, [assessment]);


  async function showAssessment(_assessmentObject: IAssessmentObject, starting:boolean=false) {
    props.setMessage(null);
    if (_assessmentObject) {
      try {
        setAssessment(null);
        let ret = await api.getAssessment(props.setGetAssessmentApiState, starting, _assessmentObject.cohort_id, _assessmentObject.lmodule_code, _assessmentObject.questions_id);
        ret.assessment_obj.shown = true;
        setAssessment(ret);
        props.setMessage(ret.message??null);
      } catch (e: any) {
        props.setMessage("Error fetching assessments");
        //console.error("Error fetching assessment:", e);
      }
    }
  }
  

  return <>
    <div className={styles.content__wrapper}>
      {/*
      {contentStructure.manual_path !== null &&
        <div className={styles.resources}>
          {
            contentStructure.manual_watermarking ? <>
              <p>Your course manual is being prepared. Please check back in a couple of minutes.</p>
            </> : <>
              <a href={contentStructure.manual_path} download>
                <img src="/static/img/files.svg" alt="Files icon" />
                Download the course manual in PDF.
              </a>
            </>
          }
        </div>
      }
      */}

      {
        !!contentStructure.chapters && <>
          <div className={styles.courses}>
            <div style={{ display: "flex" }}>
              <div style={{ float: "left", flex: "1" }}>
                {
                  contentStructure.unit === 0 ? <>
                    <h2>Lessons</h2>
                  </> : <>
                    <h2>Lessons <span className={styles.unit} title={contentStructure.next_unit_change} style={{ cursor: "help" }}>
                      (Unit {contentStructure.unit})
                    </span></h2>
                  </>
                }
              </div>
              <div
                className={styles.lessons_chevron} 
                onClick={toggleAll}
              >
                <i className={`fas ${areLessonsCollapsed ? "fa-chevron-down" : "fa-chevron-up"}`}></i>
              </div>
            </div>

            <div className={styles.list}>
              {
                Object.entries(contentStructure.chapters).map(([chapterIndex, chapter]: [string, IChapter]) => {
                  if (props.codeReviewOnly && !hasCodeReview(chapter)) {
                    return null; // Skip chapters that are not for code review
                  }
                  const chapterType = chapter.type ?? PRESENTATION;
                  let assessmentObject : IAssessmentObject|null = null;
                  if (chapterType.startsWith(ASSESSMENT) && chapter.assessment) {
                    assessmentObject = contentStructure.assessments?.[chapter.assessment] || null;
                  }
                  return <div
                    key={chapterIndex}
                    className={getChapterClassName(chapterIndex)}
                  >
                    <div
                      className={styles.course__head}
                      onClick={() => {chapterClicked(chapterIndex, chapter)}}>
                      {chapterType.startsWith(ASSESSMENT) ? (
                        <>
                          <NavLink className={getChapterLiClassName(chapterIndex, chapter)} to={getLessonLink(chapter.id)}>
                            {chapter.title}
                          </NavLink>
                          {!!assessmentObject && (
                            <>
                            {/*
                            can:{assessmentObject?.can_start ? "T" : "F"}&nbsp; 
                            sub:{assessmentObject?.submitted ? "T" : "F"}&nbsp;
                            ena:{assessmentObject?.enabled ? "T" : "F"}&nbsp;
                            act:{assessmentObject?.active ? "T" : "F"}&nbsp;
                            shw:{assessmentObject?.shown ? "T" : "F"}&nbsp;
                            */}
                            {assessmentObject?.submitted && assessmentObject.qualification && assessmentObject.qualified && assessmentObject.qualification_certificate_link && (
                              <a className={styles.qualificationIcon} href={assessmentObject.qualification_certificate_link} target="_blank" rel="noopener noreferrer">
                                  <img src="/static/img/cydrill_qualified_badge.png" alt="Cydrill Qualified"/>
                              </a>
                            )}
                            {assessmentObject.can_start && (!assessmentObject.shown || !assessmentObject.active) && !props.busy && (
                              <button
                                className={styles.assessmentStartButton}
                                onClick={() => {
                                  if (assessmentObject) {
                                    if (searchParams.get("chapter-id") !== chapter.id) 
                                    { 
                                      const lessonLink = getLessonLink(chapter.id);
                                      navigate(lessonLink, { replace: true });
                                    }
                                    showAssessment(assessmentObject, true);
                                  }
                                }}
                                disabled={!assessmentObject.enabled}
                              >{assessmentObject.active ? "Continue" : "Start"}
                              </button>
                            )}
                            </>
                          )}
                        </>
                      ) : (
                        <p className={getChapterLiClassName(chapterIndex, chapter)}>{chapter.title}</p>
                      )}
                      {!props.codeReviewOnly && chapterType.startsWith(PRESENTATION) && (

                        <div className={styles.course__count}>
                          <p>
                            <span className={
                              !chapter.done_count
                                ?
                                styles.all
                                :
                                `${styles.done} ${styles.progress}`
                            }>
                              {chapter.done_count ?? 0}
                            </span>
                            /
                            <span className={
                              !chapter.done_count
                                ?
                                styles.all
                                :
                                (Object.keys(chapter.modules).length === chapter.done_count
                                  ?
                                  `${styles.done} ${styles.progress}`
                                  :
                                  ""
                                )
                            }>
                              {Object.keys(chapter.modules).length ?? 0}
                            </span>
                          </p>
                          <i className={
                            `fas chevron ${getTogglerIconClass(isChapterShown(chapterIndex))} ${styles.chevron}`}>
                          </i>
                        </div>
                      )}
                    </div>
                    {chapterType.startsWith(PRESENTATION) && (
                      <ul className={styles.course__lessons}>
                        {Object.entries(chapter.modules).map(([moduleIndex, module]: [string, IModule]) => {
                          if (props.codeReviewOnly && !module.type?.startsWith(CODEREVIEW)) {
                            return null; // Skip modules that are not for code review
                          }
                          return <li  key={moduleIndex} 
                                      className={getLessonLiClassName(chapterIndex, chapter, moduleIndex, module)}
                                      onClick={() => moduleClicked(chapterIndex, chapter, moduleIndex, module)}
                                      onDoubleClick={() => unvisitModule(chapterIndex, moduleIndex)}>
                            <NavLink to={getLessonLink(chapter.id, module.id)}>
                              {module.title}
                            </NavLink>
                          </li>;
                        })}
                      </ul>
                    )}
                  </div>;
                })
              }
            </div>
          </div>
        </>
      }
    </div>
  </>;
}
